import { useState, useCallback } from "react";
import { request } from "@/Api";
import { convertNestedPreviewToFlatBid } from "@/grid/spreadsheet/bidConsumer/utils/convertNestedPreviewToFlatBid";

const SHEET_EXTENSIONS = [".xlsx", ".xls", ".xlsm", ".csv"];

/**
 * useBidWorkflow — manages state for the bid import workflow.
 *
 * Encapsulates:
 *  - llmBidData / wizardBidData: separate bid objects for each source
 *  - wizardConfig: config object from AI
 *  - previewData: raw excel preview data
 *  - uploadedFile: the excel file being processed
 *  - highlightDepth: visual hierarchy depth control
 *
 * @param {Object} options
 * @param {number|null} options.modelInstanceId - project ID for bid creation
 */
export default function useBidWorkflow({ modelInstanceId }) {
    const [llmBidData, setLlmBidData] = useState(null);
    const [wizardBidData, setWizardBidData] = useState(null);
    const [wizardConfig, setWizardConfig] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [highlightDepth, setHighlightDepth] = useState(null);

    /**
     * Handles a bid_preview tool_result — converts nested preview to flat format.
     * Always overrides project_id with the current project when available.
     */
    const handleBidPreviewResult = useCallback(
        (bidPreview) => {
            const flat = convertNestedPreviewToFlatBid(bidPreview);
            if (flat && modelInstanceId) flat.project_id = modelInstanceId;
            setLlmBidData(flat);
        },
        [modelInstanceId],
    );

    /**
     * Handles streaming bid preview snapshots.
     */
    const handleBidSnapshot = useCallback(
        (bidPreview) => {
            const flat = convertNestedPreviewToFlatBid(bidPreview);
            if (flat && modelInstanceId) flat.project_id = modelInstanceId;
            setLlmBidData(flat);
        },
        [modelInstanceId],
    );

    /**
     * Handles a wizard_config tool_result.
     */
    const handleWizardConfigResult = useCallback((config) => {
        setWizardConfig(config);
    }, []);

    /**
     * Handles streaming wizard config snapshots.
     */
    const handleWizardConfigSnapshot = useCallback((config) => {
        setWizardConfig(config);
    }, []);

    /**
     * Fetches the raw excel preview data for the wizard.
     */
    const fetchPreviewData = useCallback((file) => {
        setUploadedFile(file);
        const ext = (file.name || "")
            .substring(file.name.lastIndexOf("."))
            .toLowerCase();
        if (!SHEET_EXTENSIONS.includes(ext)) {
            return Promise.resolve();
        }
        const formData = new FormData();
        formData.append("sheet_file", file);

        return request
            .post("/bids/custom-excel-preview", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((res) => {
                setPreviewData(res.data);
            })
            .catch((err) => {
                console.error("Preview failed:", err);
            });
    }, []);

    /**
     * Runs the wizard configuration against the uploaded file.
     */
    const runWizardPreview = useCallback(
        (config) => {
            if (!uploadedFile) return Promise.resolve();

            const formData = new FormData();
            formData.append("sheet_file", uploadedFile);
            formData.append("mapping_config", JSON.stringify(config));

            return request
                .post("/bids/excel-to-preview", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                })
                .then((res) => {
                    setWizardBidData({
                        ...res.data,
                        project_id: modelInstanceId,
                    });
                })
                .catch((err) => {
                    console.error("Preview configuration failed:", err);
                });
        },
        [uploadedFile, modelInstanceId],
    );

    /**
     * Resets all bid state (called on thread switch).
     */
    const reset = useCallback(() => {
        setLlmBidData(null);
        setWizardBidData(null);
        setWizardConfig(null);
        setPreviewData(null);
        setUploadedFile(null);
        setHighlightDepth(null);
    }, []);

    return {
        llmBidData,
        wizardBidData,
        wizardConfig,
        previewData,
        uploadedFile,
        highlightDepth,
        isSheetFile: uploadedFile
            ? SHEET_EXTENSIONS.includes(
                  (uploadedFile.name || "")
                      .substring(uploadedFile.name.lastIndexOf("."))
                      .toLowerCase(),
              )
            : null,
        setLlmBidData,
        setWizardBidData,
        setHighlightDepth,
        handleBidPreviewResult,
        handleBidSnapshot,
        handleWizardConfigResult,
        handleWizardConfigSnapshot,
        fetchPreviewData,
        runWizardPreview,
        reset,
    };
}
